import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

/**
 * Purchasing Hub — starter Purchasing module at /purchasing.
 *
 * Four tabs:
 *   1. Orders WIP            — editable DataGrid of non-archived purchasing_orders
 *   2. Historical Log        — admin-edit grid of archived orders
 *   3. Vendor Reference      — editable list of purchasing_vendor_notes
 *   4. Per-Job Scratchpad    — picks a project, renders a textarea bound to
 *                              purchasing_scratchpads.content
 *
 * Warm-loads the four purchasing service stores + projectsStore (used for
 * project labels and the scratchpad picker). Failures are swallowed so a
 * single empty endpoint doesn't block route entry — the body component
 * shows a soft "no data yet" state instead.
 */
const PurchasingHubRoute: RouteType = {
  name: "Purchasing",
  path: "/purchasing",
  title: "Purchasing",
  layout: "/app",
  group: "Administration",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./PurchasingHub.Page"),
      title: "Purchasing",
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./PurchasingHub.Body"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    try {
      const get = (key: string): ServiceStore | undefined =>
        (rootStore as unknown as Record<string, ServiceStore | undefined>)[key];

      await Promise.allSettled([
        get("purchasingOrdersStore")?.find?.({ query: { $limit: 500 } }),
        get("purchasingOrderAttachmentsStore")?.find?.({
          query: { $limit: 500 },
        }),
        get("purchasingVendorNotesStore")?.find?.({ query: { $limit: 500 } }),
        get("purchasingScratchpadsStore")?.find?.({ query: { $limit: 500 } }),
        get("projectsStore")?.find?.({ query: { $limit: 500 } }),
      ]);
    } catch (error) {
      console.warn("[PurchasingHubRoute] warm-load failed:", error);
    }
    return true;
  },
};

export default PurchasingHubRoute;
